import { createFeatureSelector, createSelector } from "@ngrx/store";
import { C3State } from "./c3.reducer";
import { C3HardwareItem, C3Order, C3OrderedProduct, C3Product, C3ProductBillOfMaterialsLineItem, C3ProductVersion, C3ProductVersionStatus, C3VendorHardwareData, DisplayedInventoryTransactionActivity, EditC3ProductState, InventoryTransaction, InventoryTransactionType, Resource, SaveStatus, toEntityDict, ViewC3ProductState } from "@prism/prism-shared";

export const c3State = createFeatureSelector<C3State>('c3')
export const getLoadingState = createSelector(
    c3State,
    (state: C3State): number => state.loadingState
)

export const getError = createSelector(
    c3State,
    (state: C3State): any => state.error
)

export const getHardwareItems = createSelector(
    c3State,
    (state: C3State): C3HardwareItem[] => state.hardwareItems
)

export const getVendorHardwareData = createSelector(
    c3State,
    (state: C3State): C3VendorHardwareData[] => state.vendorHardwareData
)

export const getHardwareInventoryTransactions = createSelector(
    c3State,
    (state: C3State): InventoryTransaction[] => state.inventoryTransactions.filter(t => t.type === InventoryTransactionType.C3Hardware)
)

export const getProducts = createSelector(
    c3State,
    (state: C3State): C3Product[] => state.products,
)

export const getProductVersions = createSelector(
    c3State,
    (state: C3State): C3ProductVersion[] => state.productVersions
)

export const getOrders = createSelector(
    c3State,
    (state: C3State): C3Order[] => state.orders
)

export const getOrderedProducts = createSelector(
    c3State,
    (state: C3State): C3OrderedProduct[] => state.orderedProducts
)

export const getViewProductState = createSelector(
    c3State,
    (state: C3State): ViewC3ProductState => state.viewProductState,
)

export const getBillOfMaterialsLineItems = createSelector(
    c3State,
    (state: C3State): C3ProductBillOfMaterialsLineItem[] => state.billOfMaterialsLineItems
);

export const getEditProductState = createSelector(
    c3State,
    (state: C3State): EditC3ProductState => state.editProductState
)

export const getProductInventoryTransactions = createSelector(
    c3State,
    (state: C3State): InventoryTransaction[] => state.inventoryTransactions.filter(t => t.type === InventoryTransactionType.C3Product),
)

// above are selectors that ONLY use c3State
// =========================================================
// below are selectors that are built with selectors above

export const getRecentHardwareInventoryActivity = createSelector(
    getHardwareInventoryTransactions,
    (inventoryTransactions: InventoryTransaction[]): Record<string, DisplayedInventoryTransactionActivity> => {
        const _24HoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000)

        const filteredTransactions = inventoryTransactions.filter(transaction => transaction.createdOn.getTime() > _24HoursAgo.getTime())
        const uniqueEntityIds = [...new Set(filteredTransactions.map(transaction => transaction.entityId))]

        const activity: Record<string, DisplayedInventoryTransactionActivity> = {}

        uniqueEntityIds.forEach(entityId => {

            const transactionsForEntity = filteredTransactions.filter(transaction => transaction.entityId === entityId)

            const net = transactionsForEntity.reduce((acc, transaction) => (acc + transaction.change), 0)
            const increase = transactionsForEntity.filter(transaction => transaction.change > 0).reduce((acc, transaction) => (acc + transaction.change), 0)
            const decrease = transactionsForEntity.filter(transaction => transaction.change < 0).reduce((acc, transaction) => (acc + transaction.change), 0)

            activity[entityId] = {
                net,
                increase,
                decrease
            }
        })

        return activity
    }

)

export const getHardwareItemDict = createSelector(
    getHardwareItems,
    (hardwareItems: C3HardwareItem[]): Record<string, C3HardwareItem> => toEntityDict<C3HardwareItem>(hardwareItems)
)

export const getProductDict = createSelector(
    getProducts,
    (products: C3Product[]): Record<string, C3Product> => toEntityDict<C3Product>(products)
)

export const getReleasedProductVersions = createSelector(
    getProductVersions,
    (productVersions: C3ProductVersion[]): C3ProductVersion[] => productVersions.filter((productVersion) => productVersion.status === C3ProductVersionStatus.Released)
)

export const getDraftProductVersions = createSelector(
    getProductVersions,
    (productVersions: C3ProductVersion[]): C3ProductVersion[] => productVersions.filter((productVersion) => productVersion.status === C3ProductVersionStatus.Draft)
)

export const getViewProductSelectedProduct = createSelector(
    getViewProductState,
    (state: ViewC3ProductState): C3Product => state.selectedProduct,
)

export const getViewProductSelectedProductVersions = createSelector(
    getViewProductState,
    (state: ViewC3ProductState): C3ProductVersion[] => state.selectedProductVersions,
)

export const getViewProductSelectedDrawingResource = createSelector(
    getViewProductState,
    (state: ViewC3ProductState): Resource => state.selectedDrawingResource as Resource
)

export const getViewProductSelectedProductVersionId = createSelector(
    getViewProductState,
    (state: ViewC3ProductState): string => state.selectedProductVersionId
)

export const getViewProductSelectedProductVersion = createSelector(
    getViewProductSelectedProductVersionId,
    getViewProductSelectedProductVersions,
    (selectedProductVersionId: string, productVersions: C3ProductVersion[]): C3ProductVersion => {
        return productVersions.find((productVersion) => productVersion.id === selectedProductVersionId) as C3ProductVersion
    }
)

export const getProductIdToReleasedProductVersionDict = createSelector(
    getReleasedProductVersions,
    (versions: C3ProductVersion[]): Record<string, C3ProductVersion> => toEntityDict<C3ProductVersion>(versions, 'productId')
)

export const getProductVersionProductIdDict = createSelector(
    getProductVersions,
    (productVersions: C3ProductVersion[]): Record<string, C3ProductVersion> => toEntityDict(productVersions, 'productId')
)

export const getViewProductBillOfMaterialsLineItems = createSelector(
    getViewProductState,
    (state: ViewC3ProductState): C3ProductBillOfMaterialsLineItem[] => state.billOfMaterialLineItems
)

export const getViewProductSelectedProductVersionBillOfMaterialLineItems = createSelector(
    getViewProductSelectedProductVersionId,
    getViewProductBillOfMaterialsLineItems,
    (selectedProductVersionId: string, lineItems: C3ProductBillOfMaterialsLineItem[]): C3ProductBillOfMaterialsLineItem[] => {
        return lineItems.filter(lineItem => lineItem.productVersionId === selectedProductVersionId)
    }
)

export const getViewProductProductVersionsReferencingSelectedProduct = createSelector(
    getViewProductState,
    (state: ViewC3ProductState) => state.productVersionsReferencingSelectedProduct,
)

export const getProductIdToProductVersionDict = createSelector(
    getProductVersions,
    (versions: C3ProductVersion[]): Record<string, C3ProductVersion> => toEntityDict<C3ProductVersion>(versions, 'productId')
)

export const getEditProductProductVersion = createSelector(
    getEditProductState,
    (state: EditC3ProductState): C3ProductVersion => state.productVersion
)

export const getEditProductBillOfMaterialsLineItems = createSelector(
    getEditProductState,
    (state: EditC3ProductState): C3ProductBillOfMaterialsLineItem[] => state.billOfMaterialLineItems
)

export const getEditProductBillOfMaterialsLineItemIdsToDelete = createSelector(
    getEditProductState,
    (state: EditC3ProductState): string[] => state.billOfMaterialLineItemIdsToDelete
)

export const getEditProductSaveStatus = createSelector(
    getEditProductState,
    (state: EditC3ProductState): SaveStatus => state.saveStatus,
)

export const getRecentProductInventoryActivity = createSelector(
    getProductInventoryTransactions,
    (inventoryTransactions: InventoryTransaction[]): Record<string, DisplayedInventoryTransactionActivity> => {
        const _24HoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000)

        const filteredTransactions = inventoryTransactions.filter(transaction => transaction.createdOn.getTime() > _24HoursAgo.getTime())
        const uniqueEntityIds = [...new Set(filteredTransactions.map(transaction => transaction.entityId))]

        const activity: Record<string, DisplayedInventoryTransactionActivity> = {}

        uniqueEntityIds.forEach(entityId => {

            const transactionsForEntity = filteredTransactions.filter(transaction => transaction.entityId === entityId)

            const net = transactionsForEntity.reduce((acc, transaction) => (acc + transaction.change), 0)
            const increase = transactionsForEntity.filter(transaction => transaction.change > 0).reduce((acc, transaction) => (acc + transaction.change), 0)
            const decrease = transactionsForEntity.filter(transaction => transaction.change < 0).reduce((acc, transaction) => (acc + transaction.change), 0)

            activity[entityId] = {
                net,
                increase,
                decrease
            }
        })

        return activity
    }

)

export const getOrderDict = createSelector(
    getOrders,
    (orders: C3Order[]): Record<string, C3Order> => toEntityDict<C3Order>(orders)
)
